import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import { apiGet, apiPost } from '../services/api';
import { postAnalyticsSession, trackEvent } from '../utils/analytics';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within an AuthProvider');
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const clearAuth = useCallback(() => {
    setUser(null);
    setIsAuthenticated(false);
    localStorage.removeItem('user');
  }, []);

  // Fix #17: On page load, restore session from httpOnly cookie via /api/auth/me
  useEffect(() => {
    // Pre-populate user display data from cache, but do NOT mark as authenticated yet.
    // Authentication is confirmed only by the /me response — prevents race condition
    // where stale localStorage causes admin data fetches with an expired token.
    const storedUser = localStorage.getItem('user');
    if (storedUser) {
      try { setUser(JSON.parse(storedUser)); } catch {}
    }

    apiGet('/auth/me')
      .then((data) => {
        if (data?.user) {
          setUser(data.user);
          setIsAuthenticated(true);
          localStorage.setItem('user', JSON.stringify(data.user));
          // Extend session on every page load so token never silently expires during active use
          apiPost('/auth/refresh').catch(() => {});
        } else {
          setUser(null);
          setIsAuthenticated(false);
          localStorage.removeItem('user');
        }
      })
      .catch(() => {
        // Not authenticated (401) or network error — either way, treat as logged out.
        setUser(null);
        setIsAuthenticated(false);
        localStorage.removeItem('user');
      })
      .finally(() => setIsLoading(false));
  }, []);

  const loginWithData = useCallback((userData) => {
    localStorage.setItem('user', JSON.stringify(userData));
    setUser(userData);
    setIsAuthenticated(true);
    // Pažymi esamą (galėjusią prasidėti anonimiškai) sesiją šiuo vartotoju +
    // įrašo aiškų 'login' įvykį laiko juostoje (žr. src/hooks/useAnalytics.js,
    // /admin/statistika).
    trackEvent('login');
    postAnalyticsSession();
  }, []);

  const login = useCallback(async (email, password) => {
    const data = await apiPost('/auth/login', { email, password });
    if (data.twoFactorRequired) return data; // 2FA needed — caller handles OTP step
    loginWithData(data.user);
    return data;
  }, [loginWithData]);

  const register = useCallback(async (name, email, password) => {
    const data = await apiPost('/auth/register', { name, email, password });
    localStorage.setItem('user', JSON.stringify(data.user));
    setUser(data.user);
    setIsAuthenticated(true);
    // Žr. loginWithData komentarą aukščiau — tas pats principas, tik 'register'
    // įvykiu (žr. /admin/statistika — "kur registravosi").
    trackEvent('register');
    postAnalyticsSession();
    return data;
  }, []);

  const logout = useCallback(async () => {
    trackEvent('logout');
    // Fix #17: Clear httpOnly cookie server-side
    await apiPost('/auth/logout').catch(() => {});
    localStorage.removeItem('user');
    setUser(null);
    setIsAuthenticated(false);
  }, []);

  const value = { user, isAuthenticated, isLoading, login, loginWithData, register, logout, clearAuth };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
